import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { authAPI } from '../services/api';

const PermissionContext = createContext(null);

export const PermissionProvider = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasPermissionsConfigured, setHasPermissionsConfigured] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPermissions();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchPermissions = async () => {
    try {
      const response = await authAPI.getMyPermissions();
      if (response.data.success) {
        const data = response.data.data;
        setIsAdmin(data.isAdmin || false);
        setPermissions(data.permissions || {});
        setHasPermissionsConfigured(data.hasPermissionsConfigured || false);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setIsAdmin(false);
      setPermissions({});
      setHasPermissionsConfigured(false);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (menuId, action = 'view') => {
    if (isAdmin) return true;
    
    if (!hasPermissionsConfigured) {
      return true;
    }
    
    if (!menuId || !permissions[menuId]) return false;
    
    const column = `can_${action}`;
    return permissions[menuId][column] === 1;
  };

  const canView = (menuId) => hasPermission(menuId, 'view');
  const canAdd = (menuId) => hasPermission(menuId, 'add');
  const canEdit = (menuId) => hasPermission(menuId, 'edit');
  const canDelete = (menuId) => hasPermission(menuId, 'delete');

  const value = {
    permissions,
    isAdmin,
    hasPermissionsConfigured,
    loading,
    hasPermission,
    canView,
    canAdd,
    canEdit,
    canDelete,
    refetch: fetchPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

export default PermissionContext;
